import { useEffect, useRef, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';

interface PodTerminalProps {
  backendBaseUrl: string;
  namespace: string;
  podName: string;
  container: string;
}

/**
 * K8s exec WebSocket uses v4.channel.k8s.io subprotocol:
 * - Channel 0: stdin
 * - Channel 1: stdout
 * - Channel 2: stderr
 * - Channel 3: error/status
 * Each message is prefixed with a channel byte.
 */

export const PodTerminal = ({ backendBaseUrl, namespace, podName, container }: PodTerminalProps) => {
  const termRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const triedBashRef = useRef(false);

  const connectWithShell = useCallback((shell: string, term: Terminal) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendHostname = new URL(backendBaseUrl).hostname;
    const params = new URLSearchParams({
      namespace,
      pod: podName,
      container,
      shell,
    });
    const wsUrl = `${wsProtocol}//${backendHostname}:7008/?${params}`;

    const ws = new WebSocket(wsUrl, ['v4.channel.k8s.io']);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    let gotOutput = false;

    ws.onopen = () => {
      term.writeln(`\x1b[32mConnecté (${shell}).\x1b[0m`);
      term.writeln('');

      const resizeMsg = JSON.stringify({
        Width: term.cols,
        Height: term.rows,
      });
      const resizeData = new Uint8Array(resizeMsg.length + 1);
      resizeData[0] = 4;
      new TextEncoder().encodeInto(resizeMsg, resizeData.subarray(1));
      ws.send(resizeData.buffer);
    };

    ws.onmessage = (event: MessageEvent) => {
      const data = new Uint8Array(event.data as ArrayBuffer);
      if (data.length < 1) return;
      const channel = data[0];
      const content = new TextDecoder().decode(data.subarray(1));
      if (channel === 1 || channel === 2) {
        gotOutput = true;
        term.write(content);
      } else if (channel === 3) {
        try {
          const status = JSON.parse(content);
          if (status.status === 'Failure') {
            // bash not found → fallback to sh
            if (shell === 'bash' && !triedBashRef.current) {
              triedBashRef.current = true;
              term.writeln(`\r\n\x1b[33mbash non disponible, fallback sur sh...\x1b[0m`);
              connectWithShell('sh', term);
              return;
            }
            term.writeln(`\r\n\x1b[31mErreur: ${status.message}\x1b[0m`);
          }
        } catch {
          term.writeln(`\r\n\x1b[31m${content}\x1b[0m`);
        }
      }
    };

    ws.onclose = (_event) => {
      // If bash closed immediately without output, fallback to sh
      if (shell === 'bash' && !gotOutput && !triedBashRef.current) {
        triedBashRef.current = true;
        term.writeln(`\x1b[33mbash non disponible, fallback sur sh...\x1b[0m`);
        connectWithShell('sh', term);
        return;
      }
      term.writeln('\r\n\x1b[33mDéconnecté.\x1b[0m');
    };

    ws.onerror = () => {
      term.writeln('\r\n\x1b[31mErreur de connexion WebSocket.\x1b[0m');
    };

    term.onData((input: string) => {
      if (ws.readyState === WebSocket.OPEN) {
        const encoded = new TextEncoder().encode(input);
        const msg = new Uint8Array(encoded.length + 1);
        msg[0] = 0;
        msg.set(encoded, 1);
        ws.send(msg.buffer);
      }
    });

    term.onResize(({ cols, rows }) => {
      if (ws.readyState === WebSocket.OPEN) {
        const resizeMsg = JSON.stringify({ Width: cols, Height: rows });
        const resizeData = new Uint8Array(resizeMsg.length + 1);
        resizeData[0] = 4;
        new TextEncoder().encodeInto(resizeMsg, resizeData.subarray(1));
        ws.send(resizeData.buffer);
      }
    });
  }, [backendBaseUrl, namespace, podName, container]);

  const connect = useCallback(() => {
    if (!termRef.current) return;

    if (terminalRef.current) {
      terminalRef.current.dispose();
    }
    if (wsRef.current) {
      wsRef.current.close();
    }

    const term = new Terminal({
      cursorBlink: true,
      fontSize: 13,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#d4d4d4',
        selectionBackground: '#264f78',
      },
      convertEol: true,
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(termRef.current);
    fitAddon.fit();

    terminalRef.current = term;
    fitAddonRef.current = fitAddon;
    triedBashRef.current = false;

    term.writeln('\x1b[33mConnexion au pod...\x1b[0m');

    // Try bash first, fallback to sh
    connectWithShell('bash', term);
  }, [connectWithShell]);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (terminalRef.current) terminalRef.current.dispose();
    };
  }, [connect]);

  useEffect(() => {
    const handleResize = () => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      ref={termRef}
      style={{
        height: 400,
        borderRadius: 4,
        overflow: 'hidden',
      }}
    />
  );
};
